import React, { useEffect, useState } from 'react';
import OTPInput from 'react-otp-input';
import { Pencil } from '@saleshandy/icons';
import type { IProps } from './types';
import {
  AuthHelper,
  executeOnRequestStatus,
  getIsRequestPending,
  getLeadType,
  initializeThirdPartyIntegrations,
  redirectWithoutRefresh,
} from '../../../../shared/utils';
import { isInternalUrl } from '../../../../shared/utils/is-internal-url';
import { SHLogoDark } from '../../../../shared/svg';
import { SHLogoTypography } from '../../../../shared/svg/sh-typography';
import TestimonialsCarousel from '../../../../shared/components/testimonials-carousel';
import Button from '../../../../shared/design-system/ui/button';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import toaster, { Theme } from '../../../../shared/toaster';
import { openChatSupport } from '../../../../shared/utils/open-chat-support';
import { ExceptionTypes } from '../../../../shared/types';
import { setAgencyClientRedirectURLOnLogout } from '../agency-client-login/helper';

const OTP_LENGTH = 4;
const OTP_RESEND_TIME = 60;

const MFAVerify: React.FC<IProps> = ({
  agencyConfig,
  agencyConfigRequestStatus,
  isRedirectedFromAgency,
  email,
  token,
  firstName,
  lastName,
  trackingId,
  isAuthEnabled,
  sendResendOtpRequest,
  sendVerifyOtpRequest,
  resetMFAData,
  verifyOtpRequestStatus,
  verifyOtpRequestError,
  resendOtpRequestStatus,
  sendVerifyOtpForAuthRequest,
  verifyOtpForAuthRequestStatus,
  verifyOtpForAuthRequestError,
  location,
}) => {
  const [otp, setOtp] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [resendTimer, setResendTimer] = useState(OTP_RESEND_TIME);

  // Read utm_source and MCP returnUrl from URL
  const searchParams = new URLSearchParams(location?.search || '');
  const utmSource = searchParams?.get('utm_source') || '';
  const leadParam = searchParams?.get('lead') || '';
  const leadParamType = searchParams?.get('type') || 'social';
  const mcpReturnUrl = searchParams?.get('returnUrl') || '';

  const isWhitelabel = !!agencyConfig?.clientBaseUrl;
  const isAgencyLoading =
    getIsRequestPending(agencyConfigRequestStatus) && !isInternalUrl();
  const isVerifyBtnLoading = getIsRequestPending(verifyOtpRequestStatus);
  const isVerifyBtnDisabled =
    isVerifyBtnLoading || otp?.length < OTP_LENGTH || !!error;

  const onResend = () => {
    if (!getIsRequestPending(resendOtpRequestStatus)) {
      sendResendOtpRequest(email);
      setResendTimer(OTP_RESEND_TIME);
    }
  };

  const onInputChange = (inputOtp: string) => {
    setOtp(inputOtp);
    setError('');
  };

  const onVerify = () => {
    if (isAuthEnabled) {
      sendVerifyOtpForAuthRequest(email, otp, utmSource);
    } else {
      sendVerifyOtpRequest(email, otp, utmSource);
    }
  };

  const redirectToLoginPage = () => {
    redirectWithoutRefresh(isRedirectedFromAgency ? '/agency/login' : '/login');
  };

  const onEditAction = () => {
    redirectToLoginPage();
  };

  useEffect(() => {
    if (!email) {
      redirectToLoginPage();
    }
  }, [email]);

  useEffect(() => {
    if (!isVerifyBtnDisabled) {
      if (isAuthEnabled) {
        sendVerifyOtpForAuthRequest(email, otp, utmSource);
      } else {
        sendVerifyOtpRequest(email, otp, utmSource);
      }
    }
  }, [otp]);

  useEffect(() => {
    let timeout: NodeJS.Timeout = null;
    if (resendTimer > 0) {
      timeout = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timeout);
  }, [resendTimer]);

  useEffect(() => {
    executeOnRequestStatus({
      status: isAuthEnabled
        ? verifyOtpForAuthRequestStatus
        : verifyOtpRequestStatus,
      onSuccess: () => {
        // MCP OAuth: show success page then redirect to authorize endpoint
        if (mcpReturnUrl && token) {
          AuthHelper.login({ token, redirect: false });
          sessionStorage.setItem('mcp_token', token);
          const successUrl = `/mcp-auth-success?returnUrl=${encodeURIComponent(mcpReturnUrl)}`;
          redirectWithoutRefresh(successUrl);
          return;
        }

        if (token) {
          let redirectUrl = '';
          const isUtmSourceEmailVerifier = utmSource === 'email-verifier';
          const isUtmSourceEmailSpamChecker = utmSource === 'email-spam-checker';
          const isUtmSourceGrowthHub = utmSource === 'growth-hub';

          if (isUtmSourceEmailVerifier) {
            redirectUrl = '/email-verifier?page=1&limit=25';
          } else if (isUtmSourceEmailSpamChecker) {
            redirectUrl = '/inbox-radar';
          } else if (isUtmSourceGrowthHub) {
            redirectUrl = '/growth-hub';
          }

          if (leadParam) {
            redirectUrl = `/leads?start=1&take=25&${getLeadType(
              leadParamType || 'social',
            )}=${leadParam}`;
          }

          AuthHelper.login({
            token,
            ...(redirectUrl ? { redirectUrl } : {}),
          });

          if (isRedirectedFromAgency) {
            setAgencyClientRedirectURLOnLogout();
          }

          initializeThirdPartyIntegrations({
            trackingId,
            showChatSupport: agencyConfig?.showChatSupport,
            firstName,
            lastName,
            email,
          });
        }
      },
      onFailed: () => {
        if (
          isAuthEnabled
            ? verifyOtpForAuthRequestError?.message?.includes('support')
            : verifyOtpRequestError?.message?.includes('support')
        ) {
          toaster.error(
            <div>
              Your account is suspended, to revoke this suspension feel free to
              reach out to{' '}
              <span
                className="font-bold red-txt-13"
                {...accessibleOnClick(() => {
                  openChatSupport();
                })}
              >
                <u>support</u>
              </span>
            </div>,
            {
              theme: Theme.New,
              showCloseIcon: true,
              delay: 1000000,
            },
          );
        } else if (
          isAuthEnabled
            ? verifyOtpForAuthRequestError.type === ExceptionTypes.Silent &&
            verifyOtpForAuthRequestError.message
            : verifyOtpRequestError.type === ExceptionTypes.Silent &&
            verifyOtpRequestError.message
        ) {
          setError(
            isAuthEnabled
              ? verifyOtpForAuthRequestError.message
              : verifyOtpRequestError.message,
          );
        }
      },
    });
  }, [verifyOtpRequestStatus, verifyOtpForAuthRequestStatus]);

  useEffect(
    () => () => {
      resetMFAData();
    },
    [],
  );

  return (
    <>
      {isAgencyLoading || isWhitelabel || isRedirectedFromAgency ? null : (
        <div className="testimonials-container">
          <div className="d-flex align-items-center">
            <SHLogoDark />
            <div className="bs-ml-10">
              <SHLogoTypography />
            </div>
          </div>

          <TestimonialsCarousel />
          <p className="copyright">
            &copy;{`${new Date().getFullYear()} Saleshandy`}
          </p>
        </div>
      )}

      <div
        className={
          isAgencyLoading || isWhitelabel || isRedirectedFromAgency
            ? 'auth-wrapper w-100 d-flex'
            : 'auth-wrapper'
        }
      >
        <div className="auth-container">
          <div className="auth-form mfa-form">
            <h1 className="auth-container--title verification-title">
              Verification Code
            </h1>

            <div className="mfa-form--message-container">
              <div className="message">
                Please enter the code we have sent to
              </div>
              <div className="email-container">
                <span className="email">{email}</span>
                <Pencil
                  height={16}
                  width={16}
                  className="edit-action"
                  {...accessibleOnClick(onEditAction)}
                />
              </div>
            </div>

            <div>
              <OTPInput
                value={otp}
                onChange={onInputChange}
                shouldAutoFocus={true}
                inputType="number"
                numInputs={OTP_LENGTH}
                containerStyle="otp-container"
                inputStyle={`otp-input ${error ? 'otp-input-error' : ''}`}
                renderInput={(props) => <input {...props} />}
              />
              {error && <div className="mfa-form--error-message">{error}</div>}
            </div>

            <div className="mfa-form--hint-message">
              Please check spam or junk folder if not received in inbox
            </div>

            {!isAuthEnabled && (
              <div className="mfa-form--resend-container">
                {resendTimer <= 0 ? (
                  <span className="resend-btn" {...accessibleOnClick(onResend)}>
                    Resend Code
                  </span>
                ) : (
                  <>
                    <span className="strong-text">
                      Didn’t get verification code?
                    </span>{' '}
                    Resend code in {resendTimer.toString().padStart(2, '0')}s
                  </>
                )}
              </div>
            )}

            <Button
              isFullWidth
              isLoading={isVerifyBtnLoading}
              disabled={isVerifyBtnDisabled}
              loadingText="Verifying..."
              onClick={onVerify}
            >
              Verify
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MFAVerify;
